import {
  Getters,
  Mutations,
  Actions,
  Module,
  createMapper,
} from 'vuex-smart-module';

import moment from 'moment-timezone';

import ApiFactory from '@/api/apiFactory';

const ConfigRepository = ApiFactory.get('config');

const timeExtractor = (date: string) => {
  const [
    hours,
    minutes,
  ] = date.split(':').map((item) => parseInt(item, 10));
  const tz = moment();
  tz.hours(hours);
  tz.minutes(minutes);

  return parseInt(tz.format('x'), 10);
};

interface LangItem {
  code: string;
  default: boolean;
  flag: string;
  label: string;
  popular: boolean;
}

interface SocialNetwork {
  type: string;
  url: string;
}

interface ChatButtonPeriod {
  from: string;
  to: string;
}

interface ChatButton {
  enabled: boolean;
  period: ChatButtonPeriod | null;
}

class ConfigsState {
  langList: Array<LangItem> = [];

  chatButton: ChatButton = {
    enabled: false,
    period: null,
  };

  contactsEnabled = false;

  disabledCurrencies = {} as null | Record<string, string[]>;

  socialNetworks: Array<SocialNetwork> = [];

  timezone = '';

  paymentsUrl = '/payments';

  defaultPageUrl = '/about/';

  isLoaded = false;

  jurisdiction = null;

  punterHash = null;

  returnFromSearch = false;

  staticPaymentsUrl = '/payments-new';

  punter = {
    email: '',
    firstName: '',
    lastName: '',
    middleName: null,
    state: '',
    vip: false,
  }
}

class ConfigsGetters extends Getters<ConfigsState> {
  get chatButtonEnabled() {
    return this.state.chatButton.enabled;
  }

  get punterHash() {
    return this.state.punterHash;
  }

  get rawChatButtonEnabledPeriod() {
    return this.state.chatButton.period;
  }

  get chatButtonEnabledPeriod() {
    return (() => {
      if (this.state.chatButton.period) {
        const currentTime = parseInt(moment().format('x'), 10);
        const workFrom = timeExtractor(this.state.chatButton.period.from);
        const workTo = timeExtractor(this.state.chatButton.period.to);

        if (currentTime >= workFrom && currentTime <= workTo) {
          return true;
        }
        return false;
      }
      return false;
    });
  }

  get contactsEnabled() {
    return this.state.contactsEnabled;
  }

  get socialNetworks() {
    return this.state.socialNetworks;
  }

  get timezone() {
    return this.state.timezone;
  }

  get popularLanguage() {
    return this.state.langList
      .filter((item) => !!item.popular);
  }

  get defaultPageUrl() {
    return this.state.defaultPageUrl;
  }

  get otherLanguage() {
    return this.state.langList
      .filter((item) => !item.popular);
  }

  get currentLanguage() {
    return this.state.langList[0];
  }

  get configIsLoaded() {
    return this.state.isLoaded;
  }

  get paymentsUrl() {
    return this.state.paymentsUrl;
  }

  get staticPaymentsUrl() {
    return this.state.staticPaymentsUrl;
  }

  get jurisdiction(): string | null {
    return this.state.jurisdiction;
  }

  get punterData() {
    return this.state.punter;
  }
}

class ConfigsMutations extends Mutations<ConfigsState> {
  update(payload: any) {
    Object.keys(payload).forEach((element) => {
      switch (element) {
        case 'timezone': {
          this.state.timezone = payload[element];
          break;
        }
        case 'socialNetworks': {
          this.state.socialNetworks = [].concat(payload[element]);
          break;
        }
        case 'contactsEnabled': {
          this.state.contactsEnabled = payload[element];
          break;
        }
        case 'languages': {
          this.state.langList = [].concat(payload[element]);
          break;
        }
        case 'chatButtonEnabled': {
          this.state.chatButton.enabled = payload[element];
          break;
        }
        case 'jurisdiction': {
          this.state.jurisdiction = payload[element];
          break;
        }
        case 'disabledCurrencies': {
          this.state.disabledCurrencies = payload[element];
          break;
        }
        case 'chatButtonEnabledPeriod': {
          this.state.chatButton.period = payload[element] || null;
          break;
        }
        case 'paymentsUrl': {
          this.state.paymentsUrl = `/${payload[element].split('/').filter((item: string) => item.length).join('/')}`;
          break;
        }
        case 'punterHash': {
          this.state.punterHash = payload[element];
          break;
        }
        case 'punter': {
          this.state.punter = payload[element];
          break;
        }
        default: {
          break;
        }
      }
    });
  }

  setConfigState(state: boolean) {
    this.state.isLoaded = state;
  }

  setFromSearch(state: boolean) {
    this.state.returnFromSearch = state;
  }
}

class ConfigsActions extends Actions<
  ConfigsState,
  ConfigsGetters,
  ConfigsMutations,
  ConfigsActions
> {
  getAsyncData(code: string) {
    return new Promise((resolve, reject) => {
      ConfigRepository.get(code)
        .then((res) => {
          this.commit('update', res);
          this.commit('setConfigState', true);
          resolve();
        })
        .catch((e) => {
          this.commit('setConfigState', true);
          reject(e);
        });
    });
  }

  setFromSearch(state: boolean) {
    this.commit('setFromSearch', true);
  }
}

// Экспорт модуля
export const ConfigsModule = new Module({
  state: ConfigsState,
  getters: ConfigsGetters,
  mutations: ConfigsMutations,
  actions: ConfigsActions,
});

export const ConfigsModuleMapper = createMapper(ConfigsModule);
